import { httpsCallable } from "firebase/functions";
import { functions } from "./firebase";

export function newIdempotencyKey(): string {
  return crypto.randomUUID();
}

const friendly: Record<string, string> = {
  "auth.required": "Please sign in and try again.",
  "auth.forbidden": "You don't have permission to do that.",
  "auth.tenant_mismatch": "You don't belong to this company.",
  "input.invalid": "Something in the form isn't valid.",
  not_found: "That record no longer exists.",
  conflict: "That action conflicts with the current state.",
  "quota.exceeded": "You've hit a plan limit.",
  "integration.not_enabled": "This integration isn't enabled for your company yet.",
};

export class CallError extends Error {
  constructor(
    public code: string,
    message: string,
    public hint?: string,
  ) {
    super(message);
  }
}

// Every mutating call automatically carries an idempotency key.
export async function call<TResult = unknown>(
  name: string,
  data: Record<string, unknown> = {},
  opts: { mutation?: boolean; timeoutMs?: number } = { mutation: true },
): Promise<TResult> {
  const payload = opts.mutation !== false ? { idempotencyKey: newIdempotencyKey(), ...data } : data;
  try {
    // Long server jobs (a big cleanup) need more than the SDK's ~70s default.
    const fn = httpsCallable(functions, name, opts.timeoutMs ? { timeout: opts.timeoutMs } : undefined);
    const res = await fn(payload);
    return res.data as TResult;
  } catch (err) {
    const details = (err as { details?: { code?: string; hint?: string } }).details;
    const code = details?.code ?? "internal";
    // A server ClevisError (details.code set) carries a message written for
    // the person ("…expired Mar 1"); the generic copy is only a fallback.
    const serverMessage = details?.code ? ((err as Error).message ?? "").trim() : "";
    const message = serverMessage || friendly[code] || (err as Error).message || "Something went wrong.";
    throw new CallError(code, message, details?.hint);
  }
}
